import React, { useCallback, useEffect, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import {
  searchDrugs,
  updateDrugListState,
  resetError,
} from "./DrugSearch.action";
import { useNavigate } from "react-router-dom";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Tip from "components/Tip";
import debounce from "lodash/debounce";
import { WrapTextOnNoSpace } from "components/WrapText";
import { showNotification } from "services/NotificationService";
import ServerSideReactTable from "commons/table/ServerSideReactTable";
import { ColumnTypes } from "commons/table/Constants";

const DEFAULT_PAGE_SIZE = 15;

const DrugSearch = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

    const {
      data,
      pages,
      totalElements,
      page,
      pageSize,
      loading,
    searchTerm: searchTermFromStore,
      errorFetchingList,
  } = useSelector((state) => ({
    data: state.drugSearch.data !== undefined ? state.drugSearch.data : [],
    pages: state.drugSearch.pages ? state.drugSearch.pages : 0,
    totalElements:
      state.drugSearch.totalElements !== undefined
        ? state.drugSearch.totalElements
        : "Loading ...",
    loading: state.drugSearch.loading,
    searchTerm: state.drugSearch.searchTerm ? state.drugSearch.searchTerm : "",
    errorFetchingList: state.drugSearch.errorFetchingList,
    page: state.drugSearch.page ? state.drugSearch.page : 0,
    pageSize: state.drugSearch.pageSize
      ? state.drugSearch.pageSize
      : DEFAULT_PAGE_SIZE,
  }));

  const { codexVersionId, isWorkingVersionEditable } = useSelector((state) => ({
    codexVersionId: state.codexVersion
      ? state.codexVersion.codexVersionId
      : null,
    isWorkingVersionEditable: state.codexVersion
      ? state.codexVersion.isWorkingVersionEditable
      : null,
  }));

  const [searchTerm, setSearchTerm] = useState(searchTermFromStore || "");

  const updateData = useCallback(
    (targetPage, targetPageSize, term) => {
      dispatch(searchDrugs(targetPageSize, targetPage, term));
      dispatch(updateDrugListState(targetPage, targetPageSize));
    },
    [dispatch]
  );

  // Initial load
  useEffect(() => {
    updateData(0, pageSize, searchTerm);
  }, [updateData, pageSize, searchTerm]);

  // Error handling
  const prevErrorRef = useRef(false);
  useEffect(() => {
    if (errorFetchingList && !prevErrorRef.current) {
      showNotification({
        title: "Error fetching the results!",
        message:
          "There appears to be an issue while fetching the search results.",
        position: "bl",
        type: "error",
      });
      dispatch(resetError());
    }
    prevErrorRef.current = errorFetchingList;
  }, [errorFetchingList, dispatch]);

  // Debounced search handler
  const debouncedSearchRef = useRef(null);

  useEffect(() => {
    debouncedSearchRef.current = debounce((term) => {
      setSearchTerm(term);
      updateData(0, pageSize, term); // reset page whenever search term changes
  }, 500);

    return () => {
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current.cancel();
      }
    };
  }, [updateData, pageSize]);

  const handleSearchTermChange = useCallback((term) => {
    if (debouncedSearchRef.current) {
      debouncedSearchRef.current(term);
    }
  }, []);

  const onUpdateTable = useCallback(
    (filterValues, sortBy, targetPageSize, pageNo) => {
      updateData(pageNo, targetPageSize, searchTerm);
    },
    [searchTerm, updateData]
  );

  const filtered = [];
  const sorted = [];

    const allColumnsSortable = false;
    const allColumnsFilterable = false;

    const renderHighlightedCell = (value, row, accessor) => {
      return (
        <span>
          {row.original?.highlightedFields &&
            row.original?.highlightedFields[accessor] ? (
            <WrapTextOnNoSpace>
              <span
                dangerouslySetInnerHTML={{
                  __html: row.original.highlightedFields[accessor],
                }}
              ></span>
            </WrapTextOnNoSpace>
          ) : (
            <WrapTextOnNoSpace>{value ? value : "--"}</WrapTextOnNoSpace>
          )}
        </span>
      );
    };

    const columns = [
      {
        header: "Group Name",
        accessor: "dynamicSwitchGroupName",
        accessorKey: "dynamicSwitchGroupName",
        width: 100,
        enableSorting: allColumnsSortable,
        enableColumnFilter: allColumnsFilterable,
        columnType: ColumnTypes.Custom,
        customComponent: (value, row) => {
        return renderHighlightedCell(value, row, "dynamicSwitchGroupName");
        },
      },
      {
        header: "Drug Name",
        accessor: "medName",
        accessorKey: "medName",
        width: 100,
        enableSorting: allColumnsSortable,
        enableColumnFilter: allColumnsFilterable,
        columnType: ColumnTypes.Custom,
        customComponent: (value, r) => {
        return renderHighlightedCell(value, r, "medName");
        },
      },
      {
        header: "Form",
        accessor: "medForm",
        accessorKey: "medForm",
        width: 140,
        enableSorting: allColumnsSortable,
        enableColumnFilter: allColumnsFilterable,
        columnType: ColumnTypes.Custom,
        customComponent: (value, r) => {
        return renderHighlightedCell(value, r, "medForm");
        },
      },
      {
        header: "ROA",
        accessor: "routeOfAdministration",
        accessorKey: "routeOfAdministration",
        width: 70,
        enableSorting: allColumnsSortable,
        enableColumnFilter: allColumnsFilterable,
        columnType: ColumnTypes.Custom,
        customComponent: (value, r) => {
        return renderHighlightedCell(value, r, "routeOfAdministration");
        },
      },
      {
        header: "Display Name",
        accessor: "faxDisplayName",
        accessorKey: "faxDisplayName",
        width: 70,
        enableSorting: allColumnsSortable,
        enableColumnFilter: allColumnsFilterable,
        columnType: ColumnTypes.Custom,
        customComponent: (value, r) => {
        return renderHighlightedCell(value, r, "faxDisplayName");
        },
      },
      {
        header: "Active Strengths",
        accessor: "strengthStr",
        accessorKey: "strengthStr",
        enableSorting: allColumnsSortable,
        enableColumnFilter: allColumnsFilterable,
        columnType: ColumnTypes.WrapTextOnNoSpace,
      },
      {
        header: "Type",
        accessor: "itemType",
        accessorKey: "itemType",
        width: 80,
        enableSorting: allColumnsSortable,
        enableColumnFilter: allColumnsFilterable,
        columnType: ColumnTypes.WrapTextOnNoSpace,
      },
      {
        header: "Brand/Gen",
        accessor: "brandGenType",
        accessorKey: "brandGenType",
        width: 90,
        enableSorting: allColumnsSortable,
        enableColumnFilter: allColumnsFilterable,
        columnType: ColumnTypes.WrapTextOnNoSpace,
      },
    ];
    return (
      <Container className="list-container" fluid>
        <Row>
          <Col md={12}>
            <Form.Group>
              <Form.Label>Search Drugs</Form.Label>
              <Form.Control
                // value={searchTerm}
                name="searchBar"
                placeholder="e.g. Trulicity Tablet"
                disabled={loading}
                id="searchBar"
                ref={(input) => input && input.focus()}
                onChange={(e) => handleSearchTermChange(e.target.value)}
                autoComplete="off"
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="tiny-bottom-spacer">
          <Col md={6}>
            Total Items:{" "}
            <strong id="sllist-total-count">{totalElements}</strong>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <ServerSideReactTable
              columnDefination={columns}
              data={data}
              currentPage={page}
              sorted={sorted}
              filters={filtered}
              filterable={false}
              totalPages={pages}
              totalRecords={totalElements == "loading" ? 0 : totalElements}
              loading={loading}
              enableMultiRowSelection={false}
              defaultPageSize={DEFAULT_PAGE_SIZE}
              onUpdateTableData={onUpdateTable}
              pageSizeOptions={[5, 10, 15, 20, 30, 50]}
              onRowClick={(rowInfo) => {
                if (rowInfo !== undefined) {
                  navigate(
                    `/cat/dynamic-switch-groups/${rowInfo.original.dynamicSwitchGroupId}`
                  );
                }
              }}
            />
            <Tip>
              Tip: Hold shift when sorting to multi-sort and disable sort!
            </Tip>
          </Col>
        </Row>
      </Container>
    );
};

export default DrugSearch;
